<template>
    <v-dialog
        v-model="isOpen"
        max-width="480"
        :persistent="false"
        content-class="module-search-dialog"
        @keydown.esc="closeDialog"
    >
        <v-card class="module-search-modal" :elevation="0">
            <!-- Search input -->
            <div class="module-search-input-wrap">
                <div class="module-search-input">
                    <v-icon
                        :icon="searchIcon"
                        size="18"
                        class="module-search-input__icon"
                    />
                    <input
                        ref="searchInput"
                        v-model="searchQuery"
                        class="module-search-input__field"
                        type="text"
                        placeholder="Search modules..."
                        maxlength="50"
                        autocomplete="off"
                        @keydown.down.prevent="navigateDown"
                        @keydown.up.prevent="navigateUp"
                        @keydown.enter.prevent="selectCurrent"
                    />
                </div>
            </div>

            <!-- Results -->
            <div class="module-search-body">
                <template v-if="displayResults.length > 0">
                    <p class="module-search-section-label">
                        {{ searchQuery.trim() ? "Results" : "Pages" }}
                    </p>
                    <div class="module-search-list" role="listbox">
                        <button
                            v-for="(result, index) in displayResults"
                            :key="`${result.type}-${result.id}`"
                            type="button"
                            class="module-search-item cursor-pointer"
                            :class="{
                                'module-search-item--active':
                                    index === selectedIndex,
                            }"
                            role="option"
                            :aria-selected="index === selectedIndex"
                            @click="selectResult(result)"
                            @mouseenter="selectedIndex = index"
                        >
                            <v-icon
                                :icon="arrowIcon"
                                size="16"
                                class="module-search-item__icon"
                            />
                            <span class="module-search-item__label">
                                <span
                                    v-html="
                                        highlightMatch(result.name, searchQuery)
                                    "
                                />
                                <span
                                    v-if="result.parentName"
                                    class="module-search-item__parent"
                                >
                                    {{ result.parentName }}
                                </span>
                            </span>
                        </button>
                    </div>
                </template>

                <div v-else-if="searchQuery.trim()" class="module-search-empty">
                    <p class="module-search-empty__text">
                        No results for
                        <strong>{{ searchQuery }}</strong>
                    </p>
                </div>

                <div v-else class="module-search-empty">
                    <p class="module-search-empty__text">
                        No modules available
                    </p>
                </div>
            </div>

            <!-- Footer -->
            <div class="module-search-footer">
                <kbd class="module-search-kbd" aria-hidden="true">
                    <v-icon :icon="enterIcon" size="12" />
                </kbd>
                <span class="module-search-footer__hint">Go to Page</span>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import { mdiMagnify, mdiArrowTopRight, mdiKeyboardReturn } from "@mdi/js";
import apiService from "@/apiService";
import processErrors from "@/js/ProcessErrors";

export default {
    name: "ModuleSearch",
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        projectType: {
            type: String,
            default: null,
        },
    },
    emits: ["update:modelValue", "module-selected"],
    data() {
        return {
            searchQuery: "",
            searchResults: [],
            selectedIndex: 0,
            allModules: [],
            searchIcon: mdiMagnify,
            arrowIcon: mdiArrowTopRight,
            enterIcon: mdiKeyboardReturn,
        };
    },
    computed: {
        isOpen: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit("update:modelValue", value);
            },
        },
        defaultPages() {
            // Default "Pages" list mirrors MAIN MODULES (no submodules).
            const pages = this.allModules
                .filter(
                    (module) =>
                        (!module.submodule || module.submodule.length === 0) &&
                        module.link &&
                        module.link !== "-" &&
                        module.link !== "/UserProfile",
                )
                .map((module) => ({
                    id: module.id,
                    name: module.name,
                    link: module.link,
                    icon: module.icon,
                    type: "module",
                    parentName: null,
                }));

            const hasHome = pages.some(
                (p) =>
                    p.name === "Home" || p.link === "/" || p.link === "/home",
            );
            if (!hasHome) {
                pages.unshift({
                    id: "home",
                    name: "Home",
                    link: "/",
                    icon: null,
                    type: "module",
                    parentName: null,
                });
            }

            return pages.slice(0, 12);
        },
        displayResults() {
            if (this.searchQuery.trim()) {
                return this.searchResults;
            }
            return this.defaultPages;
        },
    },
    watch: {
        isOpen(newVal) {
            if (newVal) {
                this.loadModules();
                this.$nextTick(() => {
                    this.focusSearchInput();
                });
            } else {
                this.resetSearch();
            }
        },
        searchQuery(newVal) {
            this.performSearch(newVal);
            this.selectedIndex = 0;
        },
        displayResults() {
            if (this.selectedIndex >= this.displayResults.length) {
                this.selectedIndex = Math.max(
                    0,
                    this.displayResults.length - 1,
                );
            }
        },
    },
    methods: {
        async loadModules() {
            if (!this.projectType) return;

            try {
                const params = {
                    type: this.projectType,
                };
                const response = await apiService("modules", "get", params);
                if (response.status === false) {
                    processErrors(response);
                } else {
                    this.allModules = response.data.modules;
                    this.performSearch(this.searchQuery);
                }
            } catch (error) {
                processErrors(error);
            }
        },
        performSearch(query) {
            if (!query.trim()) {
                this.searchResults = [];
                return;
            }

            const results = [];
            const searchTerm = query.toLowerCase();

            this.allModules.forEach((module) => {
                if (!module.submodule || module.submodule.length === 0) {
                    if (
                        module.link &&
                        module.link !== "-" &&
                        module.name.toLowerCase().includes(searchTerm)
                    ) {
                        results.push({
                            id: module.id,
                            name: module.name,
                            link: module.link,
                            icon: module.icon,
                            type: "module",
                            parentName: null,
                        });
                    }
                } else {
                    module.submodule.forEach((submodule) => {
                        if (
                            submodule.name.toLowerCase().includes(searchTerm) ||
                            module.name.toLowerCase().includes(searchTerm)
                        ) {
                            results.push({
                                id: submodule.id,
                                name: submodule.name,
                                link: submodule.link,
                                icon: module.icon,
                                type: "submodule",
                                parentName: module.name,
                                parentId: module.id,
                            });
                        }
                    });
                }
            });

            results.sort((a, b) => {
                const aExact = a.name.toLowerCase() === searchTerm;
                const bExact = b.name.toLowerCase() === searchTerm;
                if (aExact && !bExact) return -1;
                if (!aExact && bExact) return 1;

                const aStarts = a.name.toLowerCase().startsWith(searchTerm);
                const bStarts = b.name.toLowerCase().startsWith(searchTerm);
                if (aStarts && !bStarts) return -1;
                if (!aStarts && bStarts) return 1;

                return a.name.localeCompare(b.name);
            });

            this.searchResults = results.slice(0, 10);
        },
        navigateDown() {
            if (this.selectedIndex < this.displayResults.length - 1) {
                this.selectedIndex++;
            }
        },
        navigateUp() {
            if (this.selectedIndex > 0) {
                this.selectedIndex--;
            }
        },
        selectCurrent() {
            if (this.displayResults.length > 0 && this.selectedIndex >= 0) {
                this.selectResult(this.displayResults[this.selectedIndex]);
            }
        },
        selectResult(result) {
            this.$emit("module-selected", result);
            this.closeDialog();
        },
        closeDialog() {
            this.isOpen = false;
        },
        resetSearch() {
            this.searchQuery = "";
            this.searchResults = [];
            this.selectedIndex = 0;
        },
        focusSearchInput() {
            if (this.$refs.searchInput) {
                this.$refs.searchInput.focus();
            }
        },
        highlightMatch(text, searchTerm) {
            if (!searchTerm || !text) return text;

            const regex = new RegExp(
                `(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
                "gi",
            );
            return text.replace(regex, "<mark>$1</mark>");
        },
    },
};
</script>

<style lang="scss" scoped>
.module-search-modal {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: 12px !important;
    border: 1px solid var(--border);
    background: var(--background) !important;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06);
    color: var(--foreground);
}

.module-search-input-wrap {
    padding: 12px 12px 8px;
}

.module-search-input {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 42px;
    padding: 0 12px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--muted);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus-within {
        border-color: var(--ring);
        box-shadow: 0 0 0 3px color-mix(in oklch, var(--ring) 25%, transparent);
        background: var(--background);
    }

    &__icon {
        flex-shrink: 0;
        color: var(--muted-foreground);
    }

    &__field {
        flex: 1;
        min-width: 0;
        height: 100%;
        border: none;
        outline: none;
        background: transparent;
        color: var(--foreground);
        font-size: 14px;
        font-family: inherit;
        line-height: 1.4;

        &::placeholder {
            color: var(--muted-foreground);
        }
    }
}

.module-search-body {
    flex: 1;
    min-height: 200px;
    max-height: 360px;
    overflow-y: auto;
    padding: 4px 8px 8px;
}

.module-search-section-label {
    margin: 0;
    padding: 6px 10px 8px;
    color: var(--muted-foreground);
    font-size: 12px;
    font-weight: 500;
    line-height: 1.2;
}

.module-search-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.module-search-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 12px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: var(--foreground);
    text-align: left;
    font-size: 14px;
    font-family: inherit;
    line-height: 1.3;
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover,
    &--active {
        background: var(--muted);
    }

    &:focus-visible {
        outline: 2px solid var(--ring);
        outline-offset: 1px;
    }

    &__icon {
        flex-shrink: 0;
        color: var(--muted-foreground);
    }

    &__label {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
        font-weight: 500;
    }

    &__parent {
        color: var(--muted-foreground);
        font-size: 12px;
        font-weight: 400;
    }
}

.module-search-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 160px;
    padding: 24px;

    &__text {
        margin: 0;
        color: var(--muted-foreground);
        font-size: 13px;
        text-align: center;

        strong {
            color: var(--foreground);
            font-weight: 600;
        }
    }
}

.module-search-footer {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-top: 1px solid var(--border);
    background: var(--background);
}

.module-search-kbd {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 5px;
    border: 1px solid var(--border);
    border-radius: 5px;
    background: var(--muted);
    color: var(--muted-foreground);
    font-size: 11px;
    line-height: 1;
}

.module-search-footer__hint {
    color: var(--muted-foreground);
    font-size: 12.5px;
    font-weight: 500;
}

:deep(mark) {
    background: transparent;
    color: inherit;
    font-weight: 700;
    padding: 0;
}
</style>

<style lang="scss">
.module-search-dialog {
    box-shadow: none !important;
    overflow: visible !important;

    .v-overlay__content {
        margin-top: 12vh !important;
    }
}
</style>
